// Section 4 phase 6 (Bodies and minds), Sections 12, 16 and 23: health, strength, illness,
// happiness and trust, per person in sorted id order (streaks were updated by rationing). Deaths at health 0 are resolved
// by the lifecycle phase (phase 9); illness deaths happen here.
import { peopleSorted } from '../../core/queries';
import type { Phase } from '../../core/types';
import { updateHealth, updateStrength } from './health';
import { beginHealth, endHealth } from './healing';
import { progressIllness, rollIllness } from './illness';
import { updateHappiness, updateTrust, type TrustTally } from './mind';

export const bodiesPhase: Phase = {
  id: 'bodies',
  reads: [
    'people',
    'currentReport',
    'policies',
    'buildings',
    // Section 12 (stream M3b): medicine, triage marks and quarantine.
    'stocks',
    'triageOverrides',
    'quarantine',
  ],
  writes: [
    'people',
    'currentReport',
    'households',
    'formerPeople',
    // Section 12 (stream M3b): medicine used, isolation food, triage marks, quarantine timers.
    'stocks',
    'triageOverrides',
    'quarantine',
  ],
  projection: 'run',
  run(ctx) {
    // Section 12 (stream M3b): quarantine upkeep, triage and treatment before the per-person pass.
    beginHealth(ctx);
    for (const p of peopleSorted(ctx.draft)) {
      updateHealth(ctx, p);
      updateStrength(ctx, p);
      // Someone healthy at the start of the phase has nothing to progress (an illness caught
      // this season, by a healer in beginHealth, is not progressed until next season), so skip
      // the draft reads (Section 2 budget).
      const base = ctx.state.people[p.id];
      const ill = base === undefined || base.status.sick !== null || base.status.injured !== null;
      if (ill && !progressIllness(ctx, p)) continue;
      rollIllness(ctx, p);
      if (ctx.mode === 'projection' && p.health <= 0) ctx.report.current.expectedDeaths += 1;
    }
    // Section 12 (stream M3b): the illness multiplier as rolled, quarantine flags, triage marks.
    endHealth(ctx);
    const living = peopleSorted(ctx.draft);
    for (const p of living) updateHappiness(ctx, p);
    const tally: TrustTally = { deltas: new Map() };
    updateTrust(ctx, living, tally);
    for (const [cause, row] of [...tally.deltas.entries()].sort(([a], [b]) => (a < b ? -1 : 1))) {
      ctx.report.add({ kind: 'trust', cause, delta: row.delta / row.people, people: row.people });
    }
    return 'done';
  },
};
